'use client';

import { Activity, MapPin, RadioTower } from 'lucide-react';
import { CardContent } from '@/components/ui/card';
import { dashboardCardClassName, dashboardToneClasses, dashboardToneLabel, formatDashboardNumber } from './analytics-dashboard-style';
import type { AnalyticsHubData } from './analytics-dashboard-types';

interface NetworkHealthHeroProps {
  data: AnalyticsHubData;
}

export function NetworkHealthHero({ data }: NetworkHealthHeroProps) {
  const health = data.network_health;
  const tone = dashboardToneClasses(health.tone);
  const radius = 44;
  const circumference = 2 * Math.PI * radius;
  const progress = Math.max(0, Math.min(100, health.score));
  const dashOffset = circumference - (progress / 100) * circumference;

  return (
    <section className={dashboardCardClassName(health.tone)}>
      <CardContent className="p-4 sm:p-5 lg:p-6">
        <div className="grid gap-5 lg:grid-cols-[1.1fr_0.9fr] lg:items-center">
          <div className="min-w-0">
            <div className="inline-flex items-center gap-2 rounded-full border border-[var(--app-border)] bg-[var(--app-surface)] px-3 py-1 text-xs font-semibold text-[var(--app-text-soft)] shadow-sm backdrop-blur dark:border-[var(--app-border)] ">
              <RadioTower className="h-3.5 w-3.5" />
              {data.period.label}
            </div>
            <h1 className="mt-4 text-2xl font-bold tracking-tight text-[var(--app-text)] sm:text-3xl">
              {health.title}
            </h1>
            <p className="mt-2 max-w-xl text-sm text-[var(--app-text-soft)]">
              {health.subtitle}
            </p>
            <div className="mt-5 grid grid-cols-2 gap-2 sm:grid-cols-4">
              {[
                ['Точек', health.counters.total_locations],
                ['Открылись', health.counters.opened_locations],
                ['Не открылись', health.counters.not_opened_locations],
                ['Внимание', health.counters.attention_locations],
              ].map(([label, value]) => (
                <div key={label} className="rounded-2xl border border-[var(--app-border)] bg-[var(--app-surface)] px-3 py-2.5 shadow-sm backdrop-blur dark:border-[var(--app-border)] ">
                  <p className="text-[11px] font-medium text-[var(--app-text-soft)]">{label}</p>
                  <p className="mt-1 text-xl font-bold tabular-nums text-[var(--app-text)]">{formatDashboardNumber(Number(value))}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="flex items-center justify-center lg:justify-end">
            <div className={`relative flex h-44 w-44 items-center justify-center rounded-full bg-[var(--app-surface)] shadow-sm ring-[18px] backdrop-blur  ${tone.ring}`}>
              <svg className="absolute inset-0 h-full w-full -rotate-90" viewBox="0 0 120 120" aria-hidden>
                <circle cx="60" cy="60" r={radius} fill="none" stroke="currentColor" strokeWidth="8" className="text-white/70 dark:text-white/10" />
                <circle
                  cx="60"
                  cy="60"
                  r={radius}
                  fill="none"
                  stroke="currentColor"
                  strokeLinecap="round"
                  strokeWidth="8"
                  strokeDasharray={circumference}
                  strokeDashoffset={dashOffset}
                  className={tone.text}
                />
              </svg>
              <div className="relative text-center">
                <div className={`mx-auto mb-2 inline-flex rounded-full p-2 ${tone.soft}`}>
                  <Activity className="h-5 w-5" />
                </div>
                <p className="text-4xl font-black tabular-nums text-[var(--app-text)]">{health.score}%</p>
                <p className={`mt-1 text-xs font-bold uppercase tracking-wide ${tone.text}`}>{dashboardToneLabel(health.tone)}</p>
              </div>
              <MapPin className="absolute right-5 top-5 h-4 w-4 text-[var(--app-text-muted)]" />
            </div>
          </div>
        </div>
      </CardContent>
    </section>
  );
}

